/**
 * Broadcast Protocol for ICS Hierarchy
 *
 * Handles broadcasting commands from the command room to sector rooms.
 * Only commanders and deputies can broadcast.
 */

import * as ed from '@noble/ed25519'
import { sha512 } from '@noble/hashes/sha512'
import {
  uint8ArrayToBase64,
  base64ToUint8Array,
} from '../crypto/identity'
import type {
  TextMessage,
  VoiceMessage,
  CommandBroadcast,
  EscalationPriority,
} from '../types'

// Configure ed25519 to use sha512
ed.etc.sha512Sync = (...m) => sha512(ed.etc.concatBytes(...m))

/**
 * Create a command broadcast
 */
export async function createCommandBroadcast(
  commandRoomId: string,
  targetRoomIds: string[] | 'all',
  message: TextMessage | VoiceMessage,
  broadcastBy: string,
  broadcastByName: string,
  priority: EscalationPriority,
  privateKey: Uint8Array
): Promise<CommandBroadcast> {
  const id = crypto.randomUUID()
  const timestamp = new Date()

  // Create the payload to sign
  const payload = {
    type: 'command_broadcast',
    id,
    commandRoomId,
    targetRoomIds,
    messageId: message.id,
    priority,
    timestamp: timestamp.toISOString(),
  }

  const payloadBytes = new TextEncoder().encode(JSON.stringify(payload))
  const signature = await ed.signAsync(payloadBytes, privateKey)

  return {
    type: 'command_broadcast',
    id,
    commandRoomId,
    targetRoomIds,
    message,
    broadcastBy,
    broadcastByName,
    priority,
    timestamp,
    signature: uint8ArrayToBase64(signature),
  }
}

/**
 * Verify a command broadcast signature
 */
export async function verifyCommandBroadcast(
  broadcast: CommandBroadcast,
  publicKey: Uint8Array
): Promise<boolean> {
  try {
    const payload = {
      type: 'command_broadcast',
      id: broadcast.id,
      commandRoomId: broadcast.commandRoomId,
      targetRoomIds: broadcast.targetRoomIds,
      messageId: broadcast.message.id,
      priority: broadcast.priority,
      timestamp: broadcast.timestamp instanceof Date
        ? broadcast.timestamp.toISOString()
        : broadcast.timestamp,
    }

    const payloadBytes = new TextEncoder().encode(JSON.stringify(payload))
    const signature = base64ToUint8Array(broadcast.signature)

    return await ed.verifyAsync(signature, payloadBytes, publicKey)
  } catch {
    return false
  }
}

/**
 * Check if a sector should receive a broadcast
 */
export function shouldReceiveBroadcast(
  broadcast: CommandBroadcast,
  sectorRoomId: string
): boolean {
  if (broadcast.targetRoomIds === 'all') {
    return true
  }
  return broadcast.targetRoomIds.includes(sectorRoomId)
}

/**
 * Serialize a command broadcast for transmission
 */
export function serializeCommandBroadcast(broadcast: CommandBroadcast): Uint8Array {
  const json = JSON.stringify({
    ...broadcast,
    timestamp: broadcast.timestamp instanceof Date
      ? broadcast.timestamp.toISOString()
      : broadcast.timestamp,
    message: {
      ...broadcast.message,
      timestamp: broadcast.message.timestamp instanceof Date
        ? broadcast.message.timestamp.toISOString()
        : broadcast.message.timestamp,
    },
  })
  return new TextEncoder().encode(json)
}

/**
 * Deserialize a command broadcast from transmission
 */
export function deserializeCommandBroadcast(data: Uint8Array): CommandBroadcast {
  const json = new TextDecoder().decode(data)
  const parsed = JSON.parse(json)

  return {
    ...parsed,
    timestamp: new Date(parsed.timestamp),
    message: {
      ...parsed.message,
      timestamp: new Date(parsed.message.timestamp),
    },
  }
}

/**
 * Create a text message for broadcast
 */
export function createBroadcastTextMessage(
  content: string,
  senderId: string,
  senderName: string
): TextMessage {
  return {
    id: crypto.randomUUID(),
    type: 'text',
    senderId,
    senderName,
    senderRole: 'organizer', // Commanders are organizers in the room role system
    timestamp: new Date(),
    content,
  }
}

/**
 * Filter broadcasts by target rooms
 */
export function filterBroadcastsForSector(
  broadcasts: CommandBroadcast[],
  sectorRoomId: string
): CommandBroadcast[] {
  return broadcasts.filter((b) => shouldReceiveBroadcast(b, sectorRoomId))
}

/**
 * Sort broadcasts by timestamp (newest first)
 */
export function sortBroadcasts(broadcasts: CommandBroadcast[]): CommandBroadcast[] {
  return [...broadcasts].sort((a, b) => {
    const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime()
    const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime()
    return timeB - timeA
  })
}

/**
 * Get broadcasts within a time window
 */
export function getRecentBroadcasts(
  broadcasts: CommandBroadcast[],
  windowMs: number = 3600000 // 1 hour default
): CommandBroadcast[] {
  const cutoff = Date.now() - windowMs

  return broadcasts.filter((b) => {
    const time = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime()
    return time > cutoff
  })
}

/**
 * Group broadcasts by priority
 */
export function groupByPriority(
  broadcasts: CommandBroadcast[]
): Map<EscalationPriority, CommandBroadcast[]> {
  const grouped = new Map<EscalationPriority, CommandBroadcast[]>()

  for (const broadcast of broadcasts) {
    const existing = grouped.get(broadcast.priority) || []
    existing.push(broadcast)
    grouped.set(broadcast.priority, existing)
  }

  return grouped
}
